/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  USEVISUALEFFECTS.TS - Visual Drama & Screen Effects                      ║
 * ║  Screen shake, confetti, pulsing, and celebration effects                 ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */

import { useCallback, useState } from 'react';

// Dynamic import for canvas-confetti to handle SSR
let confetti: any = null;
if (typeof window !== 'undefined') {
  import('canvas-confetti').then(module => {
    confetti = module.default;
  });
}

type VisualEffectType = 
  | 'shake'           // Screen shake for whale purchases
  | 'shakeSmall'      // Smaller shake for regular events
  | 'confetti'        // Celebration confetti
  | 'fireworks'       // Full fireworks for round win
  | 'explosion'       // Chaos event explosion
  | 'pulse'           // Pulse effect on element
  | 'flash'           // Screen flash
  | 'bloodMoon'       // Red tint effect
  | 'ghostly';        // Ethereal effect

interface EffectState {
  shaking: boolean;
  shakeIntensity: number;
  pulsing: boolean;
  pulseColor: string;
  bloodMoonActive: boolean;
  criticalTimer: boolean;
}

const DEFAULT_STATE: EffectState = {
  shaking: false,
  shakeIntensity: 0,
  pulsing: false,
  pulseColor: 'cyan',
  bloodMoonActive: false,
  criticalTimer: false,
};

export function useVisualEffects() {
  const [state, setState] = useState<EffectState>(DEFAULT_STATE);

  // Screen shake effect
  const shake = useCallback((intensity: 'small' | 'medium' | 'large' = 'medium') => {
    const intensityMap = { small: 2, medium: 5, large: 10 };
    const duration = { small: 200, medium: 400, large: 600 };
    
    setState(s => ({ ...s, shaking: true, shakeIntensity: intensityMap[intensity] }));
    
    setTimeout(() => {
      setState(s => ({ ...s, shaking: false, shakeIntensity: 0 }));
    }, duration[intensity]);
  }, []);

  // Confetti celebration
  const celebrate = useCallback((type: 'small' | 'medium' | 'win' = 'medium') => {
    const configs = {
      small: {
        particleCount: 50,
        spread: 60,
        origin: { y: 0.7 },
      },
      medium: {
        particleCount: 100,
        spread: 100,
        origin: { y: 0.6 },
        colors: ['#00ffff', '#ff00ff', '#ffff00', '#00ff00'],
      },
      win: null, // Special handling
    };

    if (type === 'win') {
      // Full fireworks show
      const duration = 3000;
      const end = Date.now() + duration;

      const frame = () => {
        confetti({
          particleCount: 3,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: ['#00ffff', '#ff00ff', '#ffd700'],
        });
        confetti({
          particleCount: 3,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: ['#00ffff', '#ff00ff', '#ffd700'],
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      };
      frame();

      // Big burst at the end
      setTimeout(() => {
        confetti({
          particleCount: 200,
          spread: 180,
          origin: { y: 0.5 },
          colors: ['#ffd700', '#ffff00', '#ff8c00'],
        });
      }, duration - 500);
    } else {
      confetti(configs[type]!);
    }
  }, []);

  // Explosion effect for chaos events
  const explosion = useCallback((color: string = '#ff4444') => {
    shake('large');
    
    // Create explosion particles
    confetti({
      particleCount: 80,
      spread: 360,
      startVelocity: 30,
      origin: { x: 0.5, y: 0.5 },
      colors: [color, '#ff8800', '#ffff00', '#ffffff'],
      gravity: 0.5,
    });
  }, [shake]);

  // Blood moon red tint
  const setBloodMoon = useCallback((active: boolean) => {
    setState(s => ({ ...s, bloodMoonActive: active }));
  }, []);

  // Critical timer pulsing
  const setCriticalTimer = useCallback((active: boolean) => {
    setState(s => ({ ...s, criticalTimer: active }));
  }, []);

  // Pulse element
  const pulse = useCallback((color: string = '#00ffff') => {
    setState(s => ({ ...s, pulsing: true, pulseColor: color }));
    setTimeout(() => {
      setState(s => ({ ...s, pulsing: false }));
    }, 500);
  }, []);

  // Flash screen
  const flash = useCallback((color: string = '#ffffff') => {
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      inset: 0;
      background: ${color};
      opacity: 0.5;
      pointer-events: none;
      z-index: 9999;
      animation: flashFade 0.3s ease-out forwards;
    `;
    
    // Add keyframes if not exists
    if (!document.getElementById('flash-keyframes')) {
      const style = document.createElement('style');
      style.id = 'flash-keyframes';
      style.textContent = `
        @keyframes flashFade {
          from { opacity: 0.5; }
          to { opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }
    
    document.body.appendChild(overlay);
    setTimeout(() => overlay.remove(), 300);
  }, []);

  // Trigger effect by type
  const trigger = useCallback((effect: VisualEffectType, options?: any) => {
    switch (effect) {
      case 'shake':
        shake('medium');
        break;
      case 'shakeSmall':
        shake('small');
        break;
      case 'confetti':
        celebrate('medium');
        break;
      case 'fireworks':
        celebrate('win');
        break;
      case 'explosion':
        explosion(options?.color);
        break;
      case 'pulse':
        pulse(options?.color);
        break;
      case 'flash':
        flash(options?.color);
        break;
      case 'bloodMoon':
        setBloodMoon(true);
        break;
      case 'ghostly':
        flash('#8844ff');
        break;
    }
  }, [shake, celebrate, explosion, pulse, flash, setBloodMoon]);

  // CSS classes for app container
  const getContainerClasses = useCallback(() => {
    const classes: string[] = [];
    
    if (state.shaking) {
      classes.push('animate-shake');
    }
    if (state.bloodMoonActive) {
      classes.push('blood-moon-active');
    }
    if (state.criticalTimer) {
      classes.push('critical-timer-pulse');
    }
    
    return classes.join(' ');
  }, [state]);

  // CSS variables for effects
  const getCssVariables = useCallback(() => {
    return {
      '--shake-intensity': `${state.shakeIntensity}px`,
      '--pulse-color': state.pulseColor,
    } as React.CSSProperties;
  }, [state]);

  return {
    state,
    shake,
    celebrate,
    explosion,
    setBloodMoon,
    setCriticalTimer,
    pulse,
    flash,
    trigger,
    getContainerClasses,
    getCssVariables,
  };
}

// Global effect styles - add to index.css or here
export const VISUAL_EFFECT_STYLES = `
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(calc(-1 * var(--shake-intensity, 5px))); }
    20%, 40%, 60%, 80% { transform: translateX(var(--shake-intensity, 5px)); }
  }
  
  .animate-shake {
    animation: shake 0.4s ease-in-out;
  }
  
  .blood-moon-active {
    animation: bloodMoonPulse 2s ease-in-out infinite;
  }
  
  @keyframes bloodMoonPulse {
    0%, 100% { box-shadow: none; }
  }
  
  .critical-timer-pulse {
    animation: criticalPulse 0.5s ease-in-out infinite;
  }
  
  @keyframes criticalPulse {
    0%, 100% { 
      box-shadow: inset 0 0 50px rgba(255, 0, 0, 0.05);
      border-color: rgba(255, 0, 0, 0.3);
    }
    50% { 
      box-shadow: inset 0 0 100px rgba(255, 0, 0, 0.15);
      border-color: rgba(255, 0, 0, 0.6);
    }
  }
`;

export type { VisualEffectType, EffectState };
